import React, { useEffect, useState } from "react";
import "./LabelSettings.scss";
import { useGlobalSettings } from "../../../context/GlobalSettingsContext";
import {
  updateLabelSettings,
  getAllGlobalSettingsData,
} from "../../../services/api/GlobalSettingsApi";
import { useToast } from "../../../context/ToastContext";
import { useNavigate } from "react-router-dom";
import JusticeLoader from "../../../components/JusticeLoader/JusticeLoader";

const LabelSettings = () => {
  const { globalSettings, setGlobalSettings } = useGlobalSettings();
  const [settings, setSettings] = useState({
    notifyAdmin: false,
  });
  const [priorityLabels, setPriorityLabels] = useState([]);
  const [newPriorityLabel, setNewPriorityLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const { showToast } = useToast();
  const navigate = useNavigate();


  // Fetch data from API on component mount
  useEffect(() => {
    const fetchLabelSettings = async () => {
      try {
        setFetchingData(true);
        const response = await getAllGlobalSettingsData();

        if (response?.data?.result?.[0]?.labelSettings) {
          const labelSettings = response.data.result[0].labelSettings;
          setSettings({
            notifyAdmin: labelSettings.notifyAdmin || false,
          });
          setPriorityLabels(labelSettings.priorityLevel || []);

          // Update global settings if not already set
          if (!globalSettings) {
            setGlobalSettings(response.data);
          }
        } else {
          // Set default values if no data exists
          setSettings({ notifyAdmin: false });
          setPriorityLabels(["High", "Medium", "Low"]); // Default priority levels
        }
      } catch (error) {
        console.error("Error fetching label settings:", error);
        showToast("Failed to fetch label settings", "error");
        // Set defaults on error
        setSettings({ notifyAdmin: false });
        setPriorityLabels(["High", "Medium", "Low"]);
      } finally {
        setFetchingData(false);
      }
    };

    fetchLabelSettings();
  }, [globalSettings, setGlobalSettings]);

  // Update local state when globalSettings context changes
  useEffect(() => {
    if (globalSettings?.result?.[0]?.labelSettings) {
      const labelSettings = globalSettings.result[0].labelSettings;
      setSettings({
        notifyAdmin: labelSettings.notifyAdmin || false,
      });
      setPriorityLabels(labelSettings.priorityLevel || []);
    }
  }, [globalSettings]);

  const handleToggle = (field) => {
    setSettings((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleAddPriorityLabel = () => {
    if (
      newPriorityLabel.trim() &&
      !priorityLabels.includes(newPriorityLabel.trim())
    ) {
      setPriorityLabels((prev) => [...prev, newPriorityLabel.trim()]);
      setNewPriorityLabel("");
    } else if (priorityLabels.includes(newPriorityLabel.trim())) {
      showToast("Priority label already exists!", "error");
    }
  };

  const handleDeletePriorityLabel = (labelToDelete) => {
    if (priorityLabels.length > 1) {
      setPriorityLabels((prev) =>
        prev.filter((label) => label !== labelToDelete)
      );
    } else {
      showToast("At least one priority label is required!", "error");
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const updatedSettings = {
        notifyAdmin: settings.notifyAdmin,
        priorityLevel: priorityLabels,
      };

      await updateLabelSettings(updatedSettings);

      const updated = await getAllGlobalSettingsData();
      setGlobalSettings(updated.data);
      localStorage.setItem("globalSettings", JSON.stringify(updated.data));

      showToast("Label settings updated successfully.", "success");
      setTimeout(() => {
        navigate("/home/displayboard");
      }, 300);
    } catch (err) {
      console.error(err);
      showToast("Something went wrong to update Label!", "error");
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <section className="label-sett-section">
        {/* <h3 className="label-sett-title">Label Settings</h3> */}
        <JusticeLoader message="Loading label settings..." type="gavel" />
      </section>
    );
  }

  return (
    <>
      <section className="label-sett-section">
        {/* <h3 className="label-sett-title">Label Settings</h3> */}

        <div className="settings-container">
          <div className="settings-card">
            <h4 className="card-title">Notification Settings</h4>

            <div className="toggle-row">
              <label>Notify Admin</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.notifyAdmin || false}
                  onChange={() => handleToggle("notifyAdmin")}
                  id="notifyAdmin"
                />
                <label htmlFor="notifyAdmin" className="toggle-slider"></label>
              </div>
            </div>
          </div>

          <div className="settings-card">
            <h4 className="card-title">Priority Labels</h4>

            <div className="list-editor-container">
              <div className="list-input-row">
                <input
                  type="text"
                  value={newPriorityLabel}
                  onChange={(e) => setNewPriorityLabel(e.target.value)}
                  placeholder="Enter new priority label"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleAddPriorityLabel();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddPriorityLabel}
                  disabled={!newPriorityLabel.trim()}
                  className="add-item-btn"
                >
                  Add
                </button>
              </div>

              <div className="items-list">
                {priorityLabels &&
                  priorityLabels.map((label, index) => (
                    <div key={index} className="list-item">
                      <span className="item-name">{label}</span>
                      <button
                        type="button"
                        onClick={() => handleDeletePriorityLabel(label)}
                        title="Delete priority label"
                        disabled={priorityLabels.length === 1}
                        className="remove-item-btn"
                      >
                        ×
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button
            className="cancel-btn"
            onClick={() => navigate("/home/displayboard")}
          >
            Cancel
          </button>
          <button className="save-btn" onClick={handleSave} disabled={loading}>
            {loading ? "Saving..." : "Save Label Settings"}
          </button>
        </div>
      </section>
    </>
  );
};

export default LabelSettings;
